import { useState } from 'react'
import { Alert, Button, Card, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { RowActions } from '@/shared/ui/table/RowActions'
import { StaffGuestAccessModal } from './StaffGuestAccessModal'
import { ZoneFormModal } from './ZoneFormModal'
import {
  fetchZones,
  hasPortalScreen,
  methodBlockedByStrength,
  setZoneState,
  voucherNeedsSale,
  PORTAL_SUPPORTED_METHODS,
  type VenueZone,
} from './venueApi'

interface VenueZonesPanelProps {
  venueId: string
}

const STRENGTH_COLOR: Record<string, string> = {
  SUFFICIENT: 'green',
  DELEGATED: 'blue',
  INSUFFICIENT: 'orange',
}

/**
 * Ob'ekt ichidagi zonalar — bitta SSID / bitta NAS nuqtasi.
 *
 * Zonalar ro'yxati kichik (bitta ob'ektda odatda bir nechta), shuning
 * uchun `DataTable` emas — oddiy `Table`, `PlansPage` bilan bir xil andoza.
 */
export function VenueZonesPanel({ venueId }: VenueZonesPanelProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<VenueZone | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [guestZone, setGuestZone] = useState<VenueZone | null>(null)
  const [error, setError] = useState<string | null>(null)

  const zones = useQuery({
    queryKey: queryKeys.venues.zones(venueId),
    queryFn: () => fetchZones(venueId),
  })

  const toggleState = useMutation({
    mutationFn: (zone: VenueZone) => setZoneState(zone.id, zone.state === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.venues.zones(venueId) }),
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<VenueZone> = [
    {
      // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'name',
      title: t('zone.name'),
      /*
        ⚠️ ENLAR VA `scroll.x` — ustunlar siqilib ketmasligi uchun.

        Ilgari faqat oxirgi ikki ustunda en bor edi, qolgan to'rttasi
        "qolganini" bo'lib olardi. Zonalar jadvali sahifaning butun enida
        turgani bilan, kirish usullari ustuni bir necha teg bilan
        kengayadi va nomga 51px qoldirardi — zona nomi HARFLAB TIK
        yozilardi. 1000px oynada ham, telefonda ham.
      */
      width: 200,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.code}
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'nas',
      title: t('zone.nasIdentifier'),
      width: 190,
      render: (_, row) => row.nasIdentifier ?? row.ssid ?? '—',
    },
    {
      key: 'methods',
      title: t('zone.allowedMethods'),
      width: 320,
      render: (_, row) => (
        <Space size={4} wrap>
          {row.allowedMethods.map((m) => {
            // Ikki xil nosozlik, ikkalasi ham JIMGINA sodir bo'ladi:
            //   · portal ekran chizmagan  → mehmon usulni ko'rmaydi
            //   · kuchi zona talabidan past → mehmon ko'radi, kod
            //     kiritadi va RAD javobini oladi
            // Ikkinchisi yomonroq: mehmon ishlaydi deb o'ylaydi.
            const noScreen = !PORTAL_SUPPORTED_METHODS.includes(m)
            const tooWeak = methodBlockedByStrength(m, row.minStrength)
            const needsSale = m === 'VOUCHER' && voucherNeedsSale(row.allowedMethods, row.minStrength)
            const reason = tooWeak
              ? t('zone.methodTooWeak')
              : noScreen ? t('zone.methodNoPortalScreen')
              : needsSale ? t('zone.voucherNeedsSale') : null
            const tag = (
              <Tag key={m} color={tooWeak ? 'red' : noScreen ? 'orange' : needsSale ? 'blue' : undefined}>
                {t(`identificationMethod.${m}`)}
                {reason ? (needsSale && !tooWeak && !noScreen ? ' ⓘ' : ' ⚠') : null}
              </Tag>
            )
            return reason === null ? tag : (
              <Tooltip key={m} title={reason}>
                {tag}
              </Tooltip>
            )
          })}
          {/* Bitta ham chizilgan usul yo'q — mehmon boshi berk ko'chada */}
          {hasPortalScreen(row.allowedMethods) ? null : (
            <Tag color="red">{t('zone.noPortalScreenAtAll')}</Tag>
          )}
        </Space>
      ),
    },
    {
      key: 'strength',
      title: t('zone.minStrength'),
      width: 150,
      render: (_, row) => (
        <Tag color={STRENGTH_COLOR[row.minStrength]}>{t(`identificationStrength.${row.minStrength}`)}</Tag>
      ),
    },
    {
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 110,
      render: (_, row) => (
        <Tag color={row.state === 'ACTIVE' ? 'green' : 'default'}>{t(`zoneState.${row.state}`)}</Tag>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      // ⚠️ `fixed: 'right'` — sababi `VenuesPage` da. Bu yerda ham
      // "Tahrirlash"/"To'xtatish" 1440px da ekrandan chiqib ketardi:
      // kirish usullari ustuni teglar bilan kengayib, jadvalni
      // 1449px ga cho'zardi.
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: t('tariffs.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'venue.update',
            },
            {
              key: 'toggleState',
              label: row.state === 'ACTIVE' ? t('venue.suspend') : t('venue.activate'),
              onClick: () => toggleState.mutate(row),
              permission: 'venue.update',
              loading: toggleState.isPending,
            },
            {
              /*
                ⚠️ Faqat FAOL zonada: to'xtatilgan zonada grant berish
                serverda rad etiladi va amal "bosildi, ishlamadi" bo'lardi.
              */
              key: 'guestAccess',
              label: t('guestAccess.action'),
              onClick: () => setGuestZone(row),
              permission: 'venue.guest.grant',
              divider: true,
              hidden: row.state !== 'ACTIVE',
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Card
      title={t('zone.sectionTitle')}
      extra={
        <Can permission="venue.update">
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null)
              setFormOpen(true)
            }}
          >
            {t('zone.createTitle')}
          </Button>
        </Can>
      }
    >
      {error ? (
        <Alert type="error" showIcon closable style={{ marginBottom: 16 }} message={error} onClose={() => setError(null)} />
      ) : null}

      <Table<VenueZone>
        columns={columns}
        dataSource={zones.data ?? []}
        rowKey={(row) => row.id}
        loading={zones.isPending}
        size="small"
        pagination={false}
        // 56+200+190+320+150+110+56 = 1082
        scroll={{ x: 1082 }}
        locale={{ emptyText: t('zone.empty') }}
      />

      <StaffGuestAccessModal zone={guestZone} onClose={() => setGuestZone(null)} />

      <ZoneFormModal
        open={formOpen}
        venueId={venueId}
        zone={editing}
        onClose={() => setFormOpen(false)}
      />
    </Card>
  )
}
